import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import {
  BUDGET_STATUSES,
  type BudgetFilters,
  type BudgetStatus,
} from '@/features/accounting-budgets/api'
import { BudgetList } from '@/features/accounting-budgets/budget-list'
import { hasGrant } from '@/lib/me'

function isBudgetStatus(value: unknown): value is BudgetStatus {
  return typeof value === 'string' && (BUDGET_STATUSES as readonly string[]).includes(value)
}

/**
 * Route shell for the budgets list.
 *
 * The list's search and status filter live in the URL search params so a
 * filtered view is linkable and survives a reload; `BudgetList` matches them
 * client-side, because the endpoint takes no query parameters.
 *
 * `budgets.new.tsx` and `budgets.$budgetId.tsx` are CHILDREN of this route in
 * flat file routing, so without an Outlet they never mount and their URLs
 * silently render this list instead (the same shape as `bills.tsx`).
 */
export const Route = createFileRoute('/_authenticated/accounting/budgets')({
  validateSearch: (search: Record<string, unknown>): BudgetFilters => ({
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    status: isBudgetStatus(search.status) ? search.status : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'accounting_budget.view')) {
      throw redirect({ to: '/accounting' })
    }
  },
  component: AccountingBudgetsRoute,
})

function AccountingBudgetsRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <BudgetList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/accounting/budgets', search: next })}
    />
  )
}
